<template>
  <div class="side-panel" :class="{ 'side-panel-wide': sidePanelTab ==='chatLog' }">
    <div class="d-flex" style="margin-bottom: 20px;">
      <v-btn
        v-show="tabsState.goBack"
        class="mr-2"
        icon="mds:arrow-back"
        variant="tonal"
        @click="tabsState.goBack"
      />
      <v-icon v-if="tabsState.icon" :icon="tabsState.icon" class="mr-2 mt-1"></v-icon>
      <span class="side-panel-title">
        {{ tabsState.title || '' }}
      </span>
      <span v-if="sidePanelTab === 'chatLog'" class="pt-1">
        <help-dialog-chat-log />
      </span>
      <v-spacer />
      <div class="flex-shrink-0">
        <span v-if="sidePanelTab === 'chatLog'">
          <v-progress-circular
            v-if="loadingClearChatLog"
            indeterminate
            size="21"
            width="2"
          />
          <v-tooltip location="bottom" v-else>
            <template v-slot:activator="{ props }">
              <v-btn
                v-bind="props"
                :icon="'mdi-delete-outline'"
                variant="tonal"
                @click="openClearChatLogDialog = true"
                :disabled="!chatLog?.length"
              />
            </template>
            <span>Clear chat history</span>
          </v-tooltip>
        </span>
        <v-tooltip location="bottom">
          <template v-slot:activator="{ props }">
            <v-btn
              v-bind="props"
              class="ml-1"
              :icon="pinSidePanel ? 'mdi-pin-off-outline' : 'mdi-pin-outline'"
              variant="tonal"
              @click="togglePin"
            />
          </template>
          <span v-if="pinSidePanel">Unpin panel for auto-hide</span>
          <span v-else>Pin panel to keep it open</span>
        </v-tooltip>
        <v-tooltip location="bottom">
          <template v-slot:activator="{ props }">
            <v-btn
              v-bind="props"
              class="ml-1"
              icon="mds:close"
              variant="tonal"
              data-cy="close-side-panel"
              @click="close"
            />
          </template>
          <span>Close side panel</span>
        </v-tooltip>
      </div>
    </div>
    <v-tabs
      v-if="sidePanelTab !== 'chatLog'"
      :modelValue="sidePanelTab"
      @update:model-value="() => {}"
      class="new-tab"
      style="margin-bottom: 40px;"
    >
      <v-tab
        v-for="(tab, index) in visibleTabs"
        :key="index"
        :value="tab.key"
        @focus="$event.target.blur()"
        @click="openTab(tab.key)"
        tabindex="-1"
        :class="{ 'disabled-tab': !node && tab.key !== 'chatLog' }"
      >
        <v-icon size="22" icon="mdi-creation" v-if="tab.key=== 'chatLog'" />
        <span v-else>
          {{ tab.title }}
        </span>
      </v-tab>
    </v-tabs>

    <transition :name="animation">
      <div v-if="sidePanelTab === 'cards'">
        <requirement-lists
          v-if="node"
          :event="node"
          :typesToDisplayInDiagram="typesToDisplayInDiagram"
          :cards="cards"
          @showComments="showComments = $event; openTab('comments')"
          @add-card-ai="(eventId, cardTypeId) => $emit('add-card-ai', eventId, cardTypeId)"
          @update-requirement="(updatedCard) => $emit('update-requirement', updatedCard)"
        />
        <requirement-lists
          v-else
          :typesToDisplayInDiagram="typesToDisplayInDiagram"
        />
      </div>
    </transition>

    <transition :name="animation">
      <div v-if="sidePanelTab === 'dataFields'">
        <SidePanelDataFields :event="node" />
      </div>
    </transition>

    <transition :name="animation">
      <div v-if="sidePanelTab === 'fileUpload'">
        <template v-if="node?.requirementsJson?.length">
          <requirement-attachment-card
            v-for="attachment in attachments"
            class="mt-3"
            :key="attachment.id"
            :attachmentId="attachment.id"
            :type="attachment.type"
            :title="attachment.name || attachment.url"
            :date="attachment.dateCreated"
            :url="attachment.url"
            :mimeType="attachment.mimeType"
            :requirement-description="getRequirementById(attachment?.requirementId)?.description"
            @on-requirement-description-click="inspectEvent(attachment.requirementId)"
          ></requirement-attachment-card>
        </template>
        <p
          v-if="!attachments?.length"
          class="text-center text-grey-darken-1"
        >
          You have no uploads to show here. Please add attachments to cards to view uploads here. <br /><br /> NOTE: You
          cannot add attachment to <b>Events</b> directly, only to <b>Cards</b>.
        </p>
      </div>
    </transition>

    <transition :name="animation">
      <div v-if="sidePanelTab === 'comments'">
        <template v-if="selectedCard">
          <requirement-description
            style="margin-bottom: 24px;"
            :event="node"
            :item="selectedCard"
            :showAi="true"
            :showAll="true"
            :hideActions="false"
            :hideReplies="false"
            :with-dates="true"
            :reply-with-dates="true"
            :default-reply-form-visible="true"
            :showEntitySelector="true"
            :expandOnHover="true"
            compactReplies
            hideOpenInSidebar
            @keydown.stop=""
            @ask-for-reply="onAskForReply"
          >
            <text-editor
              :class="['pa-1', !cardTypeColor ? 'border-none' : '']"
              :id="selectedCard.id"
              :text="selectedCard.description"
              :disabled="$store.state.isWorkflowDisabled"
              @text-update="updateDescription($event, selectedCard.id)"
              @move-focus="moveFocus"
              @keydown.native.stop=""
              :borderColor="cardTypeColor"
            />
          </requirement-description>
        </template>
      </div>
    </transition>

    <transition :name="animation">
      <div v-if="sidePanelTab === 'chatLog'">
        <chat-log :workflow="workflow" :currentUIState="currentUIState" />
      </div>
    </transition>
    <delete-confirmation-dialog
      v-model="openClearChatLogDialog"
      title="Confirm chat log history deletion"
      text="Are you sure you want to clear chat history for this workflow?"
      :onDelete="clearChatLogHistory"
    />
  </div>
</template>

<script>
import midString from '@/utils/mid-string'
import chatLogApi from '@/api/chatLogApi'
import workflowApi from '@/api/workflowApi'
import ChatLog from '@/components/ChatLog.vue'
import { EventBus } from '@/event-bus/event-bus.js'
import requirementTypesApi from '@/api/requirementTypesApi'
import RequirementLists from '@/components/RequirementLists'
import HelpDialogChatLog from '@/components/HelpDialogChatLog.vue'
import SidePanelDataFields from '@/components/SidePanelDataFields.vue'
import RequirementDescription from '@/components/RequirementDescription'
import DeleteConfirmationDialog from '@/components/DeleteConfirmationDialog.vue'
import RequirementAttachmentCard from '@/components/RequirementAttachmentCard.vue'
import TextEditor from '@/components/TextEditor.vue'
import { mapGetters, mapState } from 'vuex'

export default {
  components: {
    HelpDialogChatLog,
    ChatLog,
    RequirementLists,
    RequirementDescription,
    DeleteConfirmationDialog,
    RequirementAttachmentCard,
    TextEditor,
    SidePanelDataFields
  },
  emits: ['add-card-ai', 'ask-for-reply', 'update-requirement'],
  props: {
    workflow: Object,
    typesToDisplayInDiagram: Array,
    node: Object,
    cards: {
      type: Array,
      default () {
        return []
      }
    },
    showDone: Boolean
  },

  data () {
    return {
      prevTab: 'cards',
      dialogOpen: false,
      pinDialog: false,
      cardsByTypes: {},
      showComments: null,
      textAreaFormLoading: false,
      focusCard: null,
      timer: null,
      openClearChatLogDialog: false,
      loadingClearChatLog: false
    }
  },

  computed: {
    ...mapState({
      sidePanelTab: state => state.sidePanelTab,
      showSidePanel: state => state.showSidePanel,
      pinSidePanel: state => state.pinSidePanel,
      chatLog: state => state.chatLog.chatLog
    }),
    ...mapGetters({
      baseData: 'svgGraphData/baseData'
    }),
    animation () {
      if (!this.showSidePanel) {
        return ''
      }

      if (this.tabsState?.tabs) {
        const prevIndex = this.tabsState.tabs.findIndex(it => it === this.prevTab)
        const nextIndex = this.tabsState.tabs.findIndex(it => it === this.sidePanelTab)
        if (prevIndex < nextIndex) {
          return 'slide-left'
        }
      }

      return 'slide-right'
    },
    tabs () {
      return [
        { key: 'cards', title: 'Cards' },
        { key: 'dataFields', title: 'Data Fields' },
        { key: 'fileUpload', title: 'File Uploads' },
        { key: 'comments', title: 'Comments' },
        { key: 'chatLog', title: 'AI Assistant' }

      ]
    },
    tabsState () {
      if (this.sidePanelTab === 'comments') {
        return {
          title: `Comments for  "${this.showComments?.card?.cardType?.title || ''} #${this.showComments?.index || 1}"`,
          tabs: ['comments'],
          goBack: () => {
            this.openTab('cards')
            this.showComments = null
          }
        }
      }

      if (this.sidePanelTab === 'chatLog') {
        return {
          icon: 'mdi-creation',
          title: 'AI Assistant (BETA)',
          tabs: ['chatLog'],
          goBack: () => this.$store.commit('setSidePanelTab', 'cards')
        }
      }

      if (this.node) {
        return {
          title: 'Details for "' + this.node.description + '"',
          tabs: ['cards', 'dataFields', 'fileUpload', 'chatLog']
        }
      } else {
        return {
          title: 'Select an event to see details',
          tabs: ['cards', 'dataFields', 'fileUpload', 'chatLog']
        }
      }
    },
    visibleTabs () {
      return this.tabs.filter((tab) => this.tabsState.tabs?.includes(tab.key))
    },
    attachments () {
      if (!this.node?.requirementsJson) {
        return []
      }

      return this.node.requirementsJson.reduce((acc, { attachments }) => {
        if (attachments) {
          acc.push(...attachments)
        }
        return acc
      }, [])
    },
    selectedCard () {
      return this.showComments && this.showComments.card
        ? this.cards.find(card => card.id === this.showComments.card.id)
        : undefined
    },
    cardTypeColor () {
      return this.selectedCard && this.selectedCard.cardType.triggerType
        ? requirementTypesApi.getBackgroundColour(this.selectedCard.cardType)
        : ''
    },
    currentUIState () {
      const tabMap = {
        0: 'Backlog',
        1: 'User Story Map',
        2: 'Domain Model',
        3: 'Entities',
        4: 'History'
      }
      const eventId = this.$route?.params?.eventId
      const events = this.workflow?.eventsJson || []
      const eventName = events.find(e => e.id === eventId)?.description || 'no selected event'
      const selectedTabNum = Number(this.$route?.query?.selectedTab) || 0
      return {
        selectedTabNum,
        selectedTabName: tabMap[selectedTabNum],
        selectedEventId: eventId,
        selectedEventName: eventName
      }
    }
  },

  watch: {
    cards (newVal) {
      this.computeCardsByType(newVal)
    },
    sidePanelTab (newVal) {
      this.$nextTick(() => {
        this.prevTab = newVal
      })
    }
  },

  mounted () {
    if (this.sidePanelTab === 'chatLog') {
      if (this.pinSidePanel) {
        this.$store.commit('pinSidePanel', false)
      }

      this.$store.commit('showSidePanel', false)
    }

    this.computeCardsByType(this.cards)
    EventBus.$on('open-card-comments', item => {
      this.$nextTick(() => {
        setTimeout(() => {
          if (item.cardId) {
            const card = this.cards.find(card => card.id === item.cardId)
            this.showComments = { card, index: 1 }
          }
        }, 0)
      })
    })
    EventBus.$on('open-card-on-event', item => {
      if (this.node && item.eventId === this.node.id) {
        this.dialogOpen = true
        this.pinDialog = true
      }
    })
    EventBus.$on('pin-dialog', item => {
      if (this.node && item.eventId === this.node.id) {
        this.pinDialog = true
      }
    })
  },

  beforeUnmount () {
    EventBus.$off('open-card-comments')
    EventBus.$off('open-card-on-event')
    EventBus.$off('pin-dialog')
  },

  methods: {
    close () {
      this.$store.commit('pinSidePanel', false)
      this.$store.commit('showSidePanel', false)
      this.$store.commit('setHasClosedSidePanel', true)
    },
    togglePin () {
      this.$store.commit('pinSidePanel', !this.pinSidePanel)
    },
    async clearChatLogHistory () {
      this.loadingClearChatLog = true
      await chatLogApi.clearChatLog({ projectId: this.workflow.projectId, workflowId: this.workflow.id })
      this.loadingClearChatLog = false
    },
    openTab (tab) {
      if (!this.$route.params.eventId && tab !== 'chatLog') {
        return
      }

      this.$store.commit('setSidePanelTab', tab)
    },
    onAskForReply (event) {
      this.$emit('ask-for-reply', Object.assign({}, event, { node: this.node }))
    },

    async submitCardForm (eventId, cardTypeId, text) {
      this.textAreaFormLoading = true
      const res = await workflowApi.createRequirementJson(this.$route.params.id, eventId, {
        cardTypeId,
        description: text
      })

      this.textAreaFormLoading = false
      if (res.requirements?.length) {
        this.focusCard = res.requirements[0].id
      }
    },

    getBackgroundColour (cardType) {
      return requirementTypesApi.getBackgroundColour(cardType)
    },

    getTextColour (cardType) {
      return requirementTypesApi.getTextColour(cardType)
    },

    openDialog () {
      this.dialogOpen = true
    },

    onKeyDown (ev) {
      if (ev.keyCode === 27) { // close menu on ESC pressed
        this.dialogOpen = false
        this.pinDialog = false
      }
    },

    onOverlayMouseover () {
      if (!this.pinDialog) {
        this.timer = setTimeout(() => {
          this.dialogOpen = false
        }, 500)
      }
    },

    computeCardsByType (allCards) {
      const typeCards = {}

      if (!allCards) {
        return {}
      }

      // add all types
      this.typesToDisplayInDiagram.forEach(cardType => {
        typeCards[cardType.id] = []
      })

      allCards
        .filter(({ done }) => this.showDone === Boolean(done))
        .forEach(card => {
          const newCard = { ...card }
          newCard.eventId = this.node.id

          const { cardType } = card

          if (!cardType) {
            return
          }

          if (!typeCards[cardType.id]) {
            typeCards[cardType.id] = []
          }

          typeCards[cardType.id].push(newCard)
        })

      for (const cards of Object.values(typeCards)) {
        cards.sort((a, b) => {
          return a.sortkey < b.sortkey
            ? -1
            : a.sortkey > b.sortkey
              ? 1
              : 0
        })
      }

      this.cardsByTypes = typeCards
    },

    onDraggableAdd (ev) {
      const { item, to, newIndex } = ev
      const { cardId } = item.dataset
      const { typeId } = to.dataset
      const card = this.node.requirementsJson.find(({ id }) => id === cardId)

      const prevCard = this.cardsByTypes[typeId][newIndex - 1]
      const nextCard = this.cardsByTypes[typeId][newIndex + 1]
      const prevSortKey = prevCard ? prevCard.sortkey : ''
      const nextSortKey = nextCard ? nextCard.sortkey : ''
      const sortkey = midString(prevSortKey, nextSortKey)

      const updatedCard = { ...card, cardType: typeId, sortkey }
      this.$emit('update-requirement', updatedCard)
    },

    onDraggableUpdate (cardTypeId, ev) {
      const { item, newIndex } = ev
      const { cardId } = item.dataset
      const card = this.node.requirementsJson.find(({ id }) => id === cardId)

      const prevCard = this.cardsByTypes[cardTypeId][newIndex - 1]
      const nextCard = this.cardsByTypes[cardTypeId][newIndex + 1]
      const prevSortKey = prevCard ? prevCard.sortkey : ''
      const nextSortKey = nextCard ? nextCard.sortkey : ''
      const sortkey = midString(prevSortKey, nextSortKey)

      const updatedCard = { ...card, sortkey }
      this.$emit('update-requirement', updatedCard)
    },

    getRequirementById (requirementId) {
      return this?.cards?.find(c => c.id === requirementId)
    },

    inspectEvent (cardId) {
      const eventId = this.baseData.cardIdToEventIdMap[cardId]
      EventBus.$emit('open-card-comments', {
        eventId,
        cardId
      })
    },

    gotoDataModels (id) {
      this.$router.push({
        path: '/workflow/' + this.$route.params.projectId + '/' + this.$route.params.id + '/' + id,
        query: { selectedTab: 2 }
      })
      this.$nextTick(() => {
        const element = document.getElementById('data-model-tab')
        if (element) {
          element.scrollIntoView({ behavior: 'smooth' })
        }
      })
    },

    moveFocus (direction, target) {
      target.firstChild.blur()
    },

    updateDescription (html, itemId) {
      workflowApi.updateRequirementsJsonMutation(this.$route.params.id, [{ requirementId: itemId, description: html }])
    }
  }
}
</script>

<style scoped>
.side-panel {
  z-index: 10;
  overflow-x: hidden;
  overflow-y: auto;
  width: 420px;
  height: calc(100vh - 24px);
  position: fixed;
  right: 12px;
  top: 12px;
  padding: 19px 16px;

  border-radius: 16px;
  background: rgb(252, 252, 252);
  box-shadow: 0 1px 4px 0 rgba(0, 0, 0, 0.15);
  transition: width ease-in-out 0.2s;
}

.side-panel.side-panel-wide {
  width: 540px;
}

.side-panel {
  .v-tab.disabled-tab {
    pointer-events: none;
    opacity: 0.5;
  }
}

.side-panel-title {
  overflow-wrap: anywhere;
  padding-top: 8px;
  color: var(--text-dark);
  font-weight: 500;
  line-height: 100%;
}

.requirement-type-title {
  border-radius: 4px;
  background: #F5F2F0;
  padding: 5px 8px;
  align-items: flex-start;
}

.requirement-type-badge {
  padding: 5px 8px;
  align-items: flex-start;
  font-weight: 500;
  border-radius: 4px;
}

.requirements-list {
  width: 100%;
}

.create-form {
  border-width: 1px;
  border-top-width: 8px;
  border-style: solid;
}

.column-title + .create-form {
  margin-top: 0 !important;
}

.cards-dialog-content.v-card--link::before {
  display: none;
}

.cards-dialog-content {
  cursor: default;
}

.slide-right-enter-active,
.slide-right-leave-active,
.slide-left-enter-active,
.slide-left-leave-active {
  transition: transform 0.2s ease-in-out;
  position: absolute;
  left: 0;
  width: 100%;
  padding: 0 16px;
}

.slide-right-enter-to,
.slide-left-enter-to,
.slide-right-leave-from,
.slide-left-leave-from {
  transform: translateX(0);
}

.slide-right-enter-from,
.slide-left-leave-to {
  transform: translateX(-370px);
}

.slide-right-leave-to,
.slide-left-enter-from {
  transform: translateX(370px);
}
</style>

<style>
.small-font {
  font-size: 12px;
}

.small-font .v-label {
  font-size: 12px;
}
</style>
